import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Building2,
  Coffee,
  Globe,
  Languages,
  Loader2,
  Mail,
  MapPin,
  Phone,
  ShoppingBag,
  Wifi,
  BadgeInfo,
  Sparkles,
  Clock3
} from 'lucide-react';

const DEFAULT_THEME = {
  backgroundStart: '#07111f',
  backgroundMid: '#0c1627',
  backgroundEnd: '#121c2f',
  accent: '#38bdf8',
  accentSoft: 'rgba(56, 189, 248, 0.14)',
  text: '#f8fafc',
  textMuted: '#cbd5e1'
};

const VENUE_CONFIG_URLS = [
  (museumId) => `/venue-pages/${encodeURIComponent(museumId)}.json`,
  (museumId) => `/museum-pages/${encodeURIComponent(museumId)}.json`,
  (museumId) => `https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app/special_itineraries/venue-pages/${encodeURIComponent(museumId)}.json`
];

const getVenueLabel = (entityType = 'venue') => {
  const normalized = String(entityType || 'venue').toLowerCase();
  if (normalized === 'museum') return 'Museum';
  if (normalized === 'monument') return 'Monument';
  if (normalized === 'business') return 'Business';
  if (normalized === 'site') return 'Site';
  if (normalized === 'place') return 'Place';
  return 'Venue';
};

const InfoCard = ({ icon: Icon, title, value }) => {
  if (!value) return null;

  return (
    <div className="group rounded-3xl border border-white/10 bg-white/6 p-5 backdrop-blur-sm transition hover:-translate-y-0.5 hover:bg-white/10">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10">
          <Icon className="h-4 w-4 text-sky-100" />
        </div>
        <h2 className="text-base font-semibold text-white">{title}</h2>
      </div>
      <p className="text-sm leading-6 text-white/72">{value}</p>
    </div>
  );
};

const MuseumInfoPage = ({ museumId }) => {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let response = null;
        for (const buildUrl of VENUE_CONFIG_URLS) {
          // eslint-disable-next-line no-await-in-loop
          const candidate = await fetch(buildUrl(museumId), { cache: 'no-store' });
          if (candidate.ok) {
            response = candidate;
            break;
          }
          if (!response) response = candidate;
        }

        if (!response || !response.ok) {
          throw new Error(`Venue page not found (${response?.status || 'unknown'})`);
        }

        const data = await response.json();
        const entityType = data.entityType || data.type || 'venue';
        const label = getVenueLabel(entityType);

        if (!isMounted) return;

        setConfig({
          museumId: data.museumId || museumId,
          entityType,
          displayName: data.displayName || data.museumName || data.name || label,
          museumName: data.museumName || data.name || label,
          museumInfo: data.museumInfo || {},
          theme: { ...DEFAULT_THEME, ...(data.theme || {}) },
          logoUrl: data.logoUrl || data.logo || '',
          heroImageUrl: data.heroImageUrl || data.heroImage || ''
        });
      } catch (loadError) {
        if (!isMounted) return;
        setError(loadError.message || 'Failed to load venue info');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, [museumId]);

  const theme = config?.theme || DEFAULT_THEME;
  const venueLabel = getVenueLabel(config?.entityType);
  const info = config?.museumInfo || {};

  const backgroundStyle = useMemo(() => ({
    background: `radial-gradient(circle at 15% 20%, ${theme.accentSoft} 0%, rgba(7, 17, 31, 0) 28%), radial-gradient(circle at 85% 12%, rgba(255,255,255,0.08) 0%, rgba(7, 17, 31, 0) 30%), linear-gradient(180deg, ${theme.backgroundStart} 0%, ${theme.backgroundMid} 52%, ${theme.backgroundEnd} 100%)`,
    color: theme.text
  }), [theme]);

  const sections = [
    { label: 'Description', value: info.description, icon: BadgeInfo },
    { label: 'Opening hours', value: info.opening_hours || info.openingHours, icon: Clock3 },
    { label: 'Admission', value: info.admission, icon: Sparkles },
    { label: 'Address', value: info.address, icon: MapPin },
    { label: 'Accessibility', value: info.accessibility, icon: Building2 },
    { label: 'Wi-Fi', value: info.wifi, icon: Wifi },
    { label: 'Gift shop', value: info.gift_shop || info.giftShop, icon: ShoppingBag },
    { label: 'Cafe', value: info.cafe, icon: Coffee },
    { label: 'Phone', value: info.phone, icon: Phone },
    { label: 'Email', value: info.email, icon: Mail },
    { label: 'Website', value: info.website, icon: Globe },
    { label: 'Language support', value: info.languages, icon: Languages }
  ].filter((item) => item.value);

  return (
    <div className="relative min-h-screen overflow-hidden" style={backgroundStyle}>
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-28 top-20 h-80 w-80 rounded-full bg-sky-300/10 blur-3xl" />
        <div className="absolute right-[-7rem] top-36 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-[-7rem] left-1/2 h-96 w-[44rem] -translate-x-1/2 rounded-full bg-indigo-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto min-h-screen w-full max-w-7xl px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
        <header className="flex items-center justify-between gap-4 pb-6">
          <a
            href={`/venue/${encodeURIComponent(museumId)}`}
            className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/10 px-4 py-2 text-sm font-medium text-white shadow-sm shadow-sky-950/10 transition hover:-translate-y-0.5 hover:bg-white/16"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to guide options
          </a>

          <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/6 px-4 py-2 text-sm text-white/70 sm:inline-flex">
            <Building2 className="h-4 w-4 text-sky-200" />
            {venueLabel} information
          </div>
        </header>

        {isLoading && (
          <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-sky-300" />
            <p className="text-sm text-white/70">Loading venue information...</p>
          </div>
        )}

        {!isLoading && !error && (
          <main className="space-y-8">
            <section className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/6 p-6 shadow-2xl shadow-sky-950/20 backdrop-blur-sm sm:p-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.14),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(56,189,248,0.12),transparent_28%)]" />
              <div className="relative z-10 grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 rounded-full border border-sky-300/20 bg-sky-300/10 px-4 py-2 text-xs font-medium uppercase tracking-[0.26em] text-sky-50">
                      <Sparkles className="h-4 w-4" />
                      Visitor details
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/8 px-4 py-2 text-xs font-medium uppercase tracking-[0.26em] text-white/72">
                      <MapPin className="h-4 w-4" />
                      {venueLabel}
                    </span>
                  </div>

                  <div className="space-y-4">
                    {config?.logoUrl ? (
                      <img
                        src={config.logoUrl}
                        alt={`${config.displayName || config.museumName || venueLabel} logo`}
                        className="h-20 w-auto object-contain drop-shadow-[0_8px_24px_rgba(15,23,42,0.35)]"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-white/10 bg-white/10">
                        <Building2 className="h-7 w-7 text-sky-100" />
                      </div>
                    )}

                    <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                      {config?.displayName || config?.museumName || venueLabel}
                    </h1>
                    <p className="max-w-2xl text-base leading-7 text-white/72">
                      {info.description || 'General visitor information for this venue.'}
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <InfoCard icon={Clock3} title="Hours" value={info.opening_hours || info.openingHours} />
                  <InfoCard icon={MapPin} title="Address" value={info.address} />
                  <InfoCard icon={Globe} title="Website" value={info.website} />
                  <InfoCard icon={Languages} title="Languages" value={info.languages} />
                </div>
              </div>
            </section>

            {sections.length > 0 ? (
              <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {sections.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="rounded-3xl border border-white/10 bg-white/6 p-5 backdrop-blur-sm transition hover:-translate-y-0.5 hover:bg-white/10">
                      <div className="mb-3 flex items-center gap-3 text-white">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10">
                          <Icon className="h-4 w-4 text-sky-200" />
                        </div>
                        <h2 className="text-base font-semibold">{item.label}</h2>
                      </div>
                      <p className="text-sm leading-6 text-white/72">{item.value}</p>
                    </div>
                  );
                })}
              </section>
            ) : (
              <section className="rounded-3xl border border-white/10 bg-white/5 p-6 text-white/70">
                No venue info has been configured yet.
              </section>
            )}
          </main>
        )}

        {error && (
          <div className="rounded-[2rem] border border-amber-400/20 bg-amber-400/10 p-4 text-sm text-amber-50">
            <p className="font-medium">Config load issue</p>
            <p className="mt-1 text-amber-100/80">{error}</p>
            <p className="mt-2 text-amber-100/70">
              Expected a config file at <code>/venue-pages/{museumId}.json</code> or <code>/museum-pages/{museumId}.json</code>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MuseumInfoPage;
